import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, throwError, switchMap, from } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';
import { isProtectedApiRequest } from './auth.interceptor';
import { getSharedFreshToken, triggerLoginRedirect } from './auth-refresh';

/** Дедупликация: трекинг показанных ошибок (module-level singleton) */
const shownErrors = new Set<string>();

/**
 * Story 88.14: URL patterns где 404 — это нормальное состояние "данных нет"
 * (optional resources). Не показываем "Resource not found" toast для них.
 * Каждый caller обрабатывает 404 локально (set(null), etc).
 */
const SUPPRESS_404_PATTERNS: RegExp[] = [
  // Feedback incidents — optional AI-generated resources
  /\/feedback\/incidents\/[^/]+\/resolution(\?|$)/,
  /\/feedback\/incidents\/[^/]+\/solution(\?|$)/,
  /\/feedback\/incidents\/[^/]+\/investigation(\?|$)/,
];

/**
 * Fix auth-401-zombie r2 (P3-a, gpt): раньше пропускались ЛЮБЫЕ URL с
 * подстрокой "/auth" — слишком широко (могло случайно накрыть будущий
 * backend-эндпоинт вроде /api/v1/auth/...). keycloak-js делает свои
 * refresh/login-запросы через СОБСТВЕННЫЙ XHR/fetch, минуя Angular
 * HttpClient — эти интерсепторы их в принципе не видят. Проверка ниже —
 * защита на случай прямого HttpClient-вызова к самому Keycloak, сужена до
 * реального realm-пути из environment (не generic "/auth"/"/token").
 */
function isKeycloakOwnRequest(req: HttpRequest<unknown>): boolean {
  return req.url.includes(`/realms/${environment.keycloak.realm}/`);
}

function showErrorToast(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  messageService: MessageService,
  translate: TranslateService,
): void {
  const errorKey = `${error.status}-${req.method}`;
  if (shownErrors.has(errorKey)) return;
  shownErrors.add(errorKey);
  // Через 5 секунд разрешаем повторный показ
  setTimeout(() => shownErrors.delete(errorKey), 5000);

  let messageKey = 'ERROR.GENERIC';
  if (error.status === 0) messageKey = 'ERROR.NETWORK';
  else if (error.status === 401) messageKey = 'ERROR.UNAUTHORIZED';
  else if (error.status === 403) messageKey = 'ERROR.FORBIDDEN';
  else if (error.status === 404) messageKey = 'ERROR.NOT_FOUND';
  else if (error.status >= 500) messageKey = 'ERROR.SERVER';

  messageService.add({
    severity: 'error',
    summary: translate.instant('ERROR.TITLE'),
    detail: translate.instant(messageKey),
    life: 5000,
  });
}

/**
 * HTTP Error Interceptor — Story BR-4
 *
 * Показывает пользователю PrimeNG toast при HTTP-ошибках.
 * Пропускает auth-эндпоинты. Дедупликация через Set + 5s TTL.
 *
 * @fix fix-auth-401-zombie: 401 в Keycloak-режиме раньше просто показывал
 * тост ERROR.UNAUTHORIZED — ни refresh, ни redirect, "зомби-сессия" (шапка
 * рисует юзера из старого state, страница не грузится). Теперь: одна
 * попытка silent refresh + retry исходного запроса; если refresh не удался
 * или повтор снова 401 — редирект на переавторизацию вместо тоста.
 * Второй уровень защиты поверх auth.interceptor (который делает то же самое
 * ПРОАКТИВНО до отправки) — на случай, если проактивная проверка токена не
 * предвидела отказ именно на сервере (рассинхрон часов, отзыв сессии и т.п.).
 * Бесконечный цикл структурно невозможен: next() здесь вызывает только
 * интерсепторы ПОСЛЕ errorInterceptor в цепочке + backend — сам
 * errorInterceptor не может быть вызван рекурсивно через собственный next().
 *
 * @fix fix-auth-401-zombie r2:
 * P0 — та же регрессия, что чинили в auth.interceptor: 401 на assets/
 * public-путях или у анонима (сессии не было) не должен форсировать
 * refresh/login — только на ЗАЩИЩЁННЫХ /api/v1/*-запросах с РЕАЛЬНО
 * существовавшей сессией (иначе fallback ниже — обычный toast).
 * P1 (gpt) — обычный getFreshToken() внутри updateToken(minValidity=30)
 * может счесть клиентский токен ещё валидным и вернуть ЕГО ЖЕ (сервер мог
 * отклонить его по ДРУГОЙ причине — рассинхрон часов, отзыв) → retry с
 * тем же токеном гарантированно получил бы второй 401. force=true →
 * updateToken(-1) — БЕЗУСЛОВНЫЙ refresh.
 *
 * @fix fix-auth-401-zombie r3 (gpt):
 * P1 — форс-рефреш теперь идёт через getSharedFreshToken(authService, true)
 * из ./auth-refresh — общий с auth.interceptor in-flight промис, а не
 * прямой вызов authService.getFreshToken(true) мимо дедупликации (иначе
 * несколько 401, разнесённых по времени, давали несколько отдельных
 * форс-рефрешей подряд).
 * P2 — triggerLoginRedirect() теперь принимает toast-fallback callback:
 * если login() сам отклонится, показываем обычный ERROR.UNAUTHORIZED тост
 * вместо того, чтобы просто тихо остаться с несброшенным состоянием.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const translate = inject(TranslateService);
  const authService = inject(AuthService);

  const redirectWithToastFallback = (): void => {
    triggerLoginRedirect(authService, () => {
      messageService.add({
        severity: 'error',
        summary: translate.instant('ERROR.TITLE'),
        detail: translate.instant('ERROR.UNAUTHORIZED'),
        life: 5000,
      });
    });
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Пропускаем прямые запросы к самому Keycloak (если такие вообще
      // случаются через Angular HttpClient — обычно нет, см. комментарий
      // isKeycloakOwnRequest) — у них своя обработка.
      if (isKeycloakOwnRequest(req)) {
        return throwError(() => error);
      }

      if (
        error.status === 401 &&
        environment.useKeycloakAuth &&
        isProtectedApiRequest(req) &&
        authService.hasEverAuthenticated()
      ) {
        return from(getSharedFreshToken(authService, true)).pipe(
          switchMap((freshToken) => {
            if (!freshToken) {
              redirectWithToastFallback();
              return throwError(() => error);
            }
            const retried = req.clone({ setHeaders: { Authorization: `Bearer ${freshToken}` } });
            return next(retried).pipe(
              catchError((retryError: HttpErrorResponse) => {
                if (retryError.status === 401) {
                  redirectWithToastFallback();
                  return throwError(() => retryError);
                }
                showErrorToast(retryError, req, messageService, translate);
                return throwError(() => retryError);
              }),
            );
          }),
        );
      }

      // Story 88.14: пропускаем 404 на optional endpoints (resolution/solution/investigation)
      // — там 404 значит "данных пока нет", caller handles локально.
      if (
        error.status === 404 &&
        SUPPRESS_404_PATTERNS.some(p => p.test(req.url))
      ) {
        return throwError(() => error);
      }

      showErrorToast(error, req, messageService, translate);
      return throwError(() => error);
    })
  );
};
